import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Space,
  Divider,
  Alert,
  Spin,
  message,
  Row,
  Col,
  Select,
  Radio,
  Checkbox,
} from 'antd';
import {
  ShoppingOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  HeartFilled,
  TagOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useStores } from '../../stores';
import ngoService from '../../services/ngoService';
import orderService from '../../services/orderService';
import cartService from '../../services/cartService';
import { getPostalCodeForCity } from '../../utils/greekPostalCodes';
import './CheckoutPage.scss';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CheckoutPage = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartStore } = useStores();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [ngos, setNgos] = useState([]);
  const [animapNgoId, setAnimapNgoId] = useState(null);
  const [loadingNgos, setLoadingNgos] = useState(true);
  const [wantsInvoice, setWantsInvoice] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Check if unified checkout mode
  const isUnifiedCheckout = location.state?.unified || false;
  const unifiedPartners = location.state?.partners || [];
  const unifiedTotalAmount = location.state?.totalAmount || 0;

  // Get partner info from navigation state (single partner mode)
  const partnerId = location.state?.partnerId;
  const partnerName = location.state?.partnerName;
  const isImpactPartner = location.state?.isImpactPartner || false;
  const impactPercentage = location.state?.impactPercentage || 0;

  useEffect(() => {
    // Redirect if no partner selected and not unified checkout
    if (!partnerId && !isUnifiedCheckout) {
      message.error(t('checkout.noPartnerSelected'));
      navigate('/cart');
      return;
    }

    // Fetch cart to ensure we have latest data
    cartStore.fetchCart();

    // Fetch NGOs for Give Back program
    const fetchNgos = async () => {
      try {
        setLoadingNgos(true);
        const ngoList = await ngoService.getActiveNGOs();
        setNgos(ngoList);
        // Set first NGO as default (Animap donation is mandatory)
        if (ngoList.length > 0 && !animapNgoId) {
          setAnimapNgoId(ngoList[0].id);
        }
      } catch (error) {
        console.error('Failed to load NGOs:', error);
      } finally {
        setLoadingNgos(false);
      }
    };
    fetchNgos();
  }, [partnerId, cartStore, navigate]);

  // Find the partner group in cart
  const partnerGroup = cartStore.cart.find(
    (group) => group.partner_id === partnerId
  );

  // Handle coupon validation
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning(t('checkout.coupon.enterCode'));
      return;
    }

    if (!partnerGroup) return;

    setValidatingCoupon(true);
    const result = await cartService.validateCoupon(
      couponCode.trim(),
      partnerId,
      parseFloat(partnerGroup.subtotal)
    );
    setValidatingCoupon(false);

    if (result.success) {
      setAppliedCoupon(result.data);
      message.success(t('checkout.coupon.applied', { code: result.data.code }));
    } else {
      message.error(result.error);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    message.info(t('checkout.coupon.removed'));
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);

    const shippingAddress = `${values.address} ${values.street_number}, ${values.city}, ${values.postal_code}, ${values.country || 'Greece'}`;

    // Prepare invoice data if requested
    const invoiceData = wantsInvoice ? {
      wants_invoice: true,
      vat_number: values.vat_number,
      company_name: values.company_name,
      profession: values.profession || '',
      tax_office: values.tax_office || '',
    } : null;

    if (isUnifiedCheckout) {
      // UNIFIED CHECKOUT - Single payment for all partners
      const result = await cartStore.unifiedCheckout(
        shippingAddress,
        values.notes || '',
        animapNgoId,
        null, // Partner NGO is handled automatically by backend
        invoiceData,
        appliedCoupon ? appliedCoupon.code : null
      );

      if (!result.success) {
        setSubmitting(false);
        message.error(result.error || t('checkout.orderFailed'));
        return;
      }

      const checkoutGroupId = result.data.checkout_group_id;
      const orderIds = result.data.order_ids;

      // Step 2: Create unified Stripe payment session
      const paymentResult = await orderService.createUnifiedPaymentSession(checkoutGroupId, orderIds);

      if (paymentResult.success) {
        // Redirect to Stripe checkout
        message.loading(t('checkout.redirectingToPayment'));
        window.location.href = paymentResult.data.checkout_url;
      } else {
        setSubmitting(false);
        message.warning(t('checkout.paymentSessionFailed'));
        navigate('/orders');
      }
    } else {
      // SINGLE PARTNER CHECKOUT
      if (!partnerGroup) {
        message.error(t('checkout.cartNotFound'));
        setSubmitting(false);
        return;
      }

      // Step 1: Create the order
      const result = await cartStore.checkout(
        partnerId,
        shippingAddress,
        values.notes || '',
        animapNgoId,
        null, // Partner NGO is handled automatically by backend
        invoiceData,
        appliedCoupon ? appliedCoupon.code : null
      );

      if (!result.success) {
        setSubmitting(false);
        message.error(result.error || t('checkout.orderFailed'));
        return;
      }

      const orderId = result.data.order.id;

      // Step 2: Create Stripe payment session
      const paymentResult = await orderService.createPaymentSession(orderId);

      if (paymentResult.success) {
        // Redirect to Stripe checkout
        message.loading(t('checkout.redirectingToPayment'));
        window.location.href = paymentResult.data.checkout_url;
      } else {
        setSubmitting(false);
        message.warning(t('checkout.paymentSessionFailed'));
        navigate('/order-confirmation', {
          state: { order: result.data.order, paymentPending: true }
        });
      }
    }
  };

  // Calculate totals for unified checkout
  const unifiedSubtotal = isUnifiedCheckout
    ? cartStore.cart.reduce((sum, g) => sum + parseFloat(g.subtotal), 0)
    : 0;
  const unifiedShipping = isUnifiedCheckout
    ? cartStore.cart.reduce((sum, g) => sum + parseFloat(g.shipping_cost), 0)
    : 0;
  const unifiedTotal = isUnifiedCheckout
    ? cartStore.cart.reduce((sum, g) => sum + parseFloat(g.total), 0)
    : 0;

  if (cartStore.loading && !partnerGroup && !isUnifiedCheckout) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('checkout.loading')} />
      </div>
    );
  }

  if (!partnerGroup && !isUnifiedCheckout) {
    return (
      <div className="sweet-container" style={{ padding: '48px 24px' }}>
        <Alert
          message={t('checkout.noItemsFound')}
          description={t('checkout.noItemsDescription')}
          type="warning"
          showIcon
        />
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/cart')}
          style={{ marginTop: 16 }}
        >
          {t('checkout.backToCart')}
        </Button>
      </div>
    );
  }

  if (isUnifiedCheckout && cartStore.cart.length === 0) {
    return (
      <div className="sweet-container" style={{ padding: '48px 24px' }}>
        <Alert
          message={t('checkout.noItemsFound')}
          description={t('checkout.noItemsDescription')}
          type="warning"
          showIcon
        />
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/cart')}
          style={{ marginTop: 16 }}
        >
          {t('checkout.backToCart')}
        </Button>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="sweet-container">
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/cart')}
          className="back-button"
        >
          {t('checkout.backToCart')}
        </Button>

        <Title level={2} style={{ marginTop: 24 }}>
          <ShoppingOutlined /> {t('checkout.title')}
        </Title>

        <Text type="secondary">
          {isUnifiedCheckout ? (
            <>
              {t('checkout.completeUnifiedOrder', { count: cartStore.cart.length })}
            </>
          ) : (
            <>
              {t('checkout.completeOrder')} <Text strong>{partnerName}</Text>
            </>
          )}
        </Text>

        <Divider />

        <Row gutter={[24, 24]}>
          {/* Checkout Form */}
          <Col xs={24} lg={14}>
            <Card title={t('checkout.shippingInfo')} className="checkout-form-card">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                  country: 'Greece',
                }}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={18}>
                    <Form.Item
                      label={t('checkout.streetAddress')}
                      name="address"
                      rules={[
                        { required: true, message: t('checkout.addressRequired') },
                      ]}
                    >
                      <Input
                        placeholder={t('checkout.addressPlaceholder')}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      label={t('checkout.streetNumber')}
                      name="street_number"
                      rules={[
                        { required: true, message: t('checkout.streetNumberRequired') },
                      ]}
                    >
                      <Input
                        placeholder="42"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={t('checkout.city')}
                      name="city"
                      rules={[
                        { required: true, message: t('checkout.cityRequired') },
                      ]}
                    >
                      <Input
                        placeholder={t('checkout.cityPlaceholder')}
                        size="large"
                        onBlur={(e) => {
                          const city = e.target.value;
                          const postalCode = getPostalCodeForCity(city);
                          if (postalCode && !form.getFieldValue('postal_code')) {
                            form.setFieldsValue({ postal_code: postalCode });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={t('checkout.postalCode')}
                      name="postal_code"
                      extra={t('checkout.postalCodeAutoFill')}
                      rules={[
                        {
                          required: true,
                          message: t('checkout.postalRequired'),
                        },
                        {
                          pattern: /^\d{5}$/,
                          message: t('checkout.postalInvalid'),
                        },
                      ]}
                    >
                      <Input placeholder="12345" size="large" maxLength={5} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label={t('checkout.country')} name="country">
                  <Input placeholder="Greece" size="large" disabled />
                </Form.Item>

                <Form.Item
                  label={t('checkout.orderNotes')}
                  name="notes"
                >
                  <TextArea
                    placeholder={t('checkout.notesPlaceholder')}
                    rows={4}
                  />
                </Form.Item>

                {/* Coupon Code - Only for single partner checkout */}
                {!isUnifiedCheckout && (
                  <Card
                    className="coupon-card"
                    style={{ marginBottom: 24, background: '#f6ffed', border: '1px solid #b7eb8f' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <TagOutlined style={{ color: '#52c41a', fontSize: 20, marginRight: 8 }} />
                      <Text strong style={{ fontSize: 16 }}>{t('checkout.coupon.title')}</Text>
                    </div>

                    {appliedCoupon ? (
                      <div>
                        <Alert
                          message={
                            <Space>
                              <CheckCircleOutlined />
                              <span>{t('checkout.coupon.appliedMessage', { code: appliedCoupon.code })}</span>
                            </Space>
                          }
                          description={
                            <div>
                              <Text>
                                {appliedCoupon.discount_type === 'PERCENTAGE'
                                  ? t('checkout.coupon.percentOff', { percent: appliedCoupon.discount_value })
                                  : t('checkout.coupon.amountOff', { amount: appliedCoupon.discount_value })}
                              </Text>
                              <br />
                              <Text strong style={{ color: '#52c41a' }}>
                                {t('checkout.coupon.youSave', { amount: appliedCoupon.discount_amount })}
                              </Text>
                            </div>
                          }
                          type="success"
                          showIcon={false}
                          action={
                            <Button
                              size="small"
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={handleRemoveCoupon}
                            >
                              {t('checkout.coupon.remove')}
                            </Button>
                          }
                        />
                      </div>
                    ) : (
                      <Space.Compact style={{ width: '100%' }}>
                        <Input
                          placeholder={t('checkout.coupon.placeholder')}
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          size="large"
                          style={{ flex: 1 }}
                          onPressEnter={handleApplyCoupon}
                        />
                        <Button
                          type="primary"
                          size="large"
                          loading={validatingCoupon}
                          onClick={handleApplyCoupon}
                          icon={<TagOutlined />}
                        >
                          {t('checkout.coupon.apply')}
                        </Button>
                      </Space.Compact>
                    )}
                  </Card>
                )}

                {/* Invoice Request */}
                <Card
                  className="invoice-card"
                  style={{ marginBottom: 24, background: '#f0f5ff', border: '1px solid #adc6ff' }}
                >
                  <Form.Item
                    name="wants_invoice"
                    valuePropName="checked"
                    style={{ marginBottom: wantsInvoice ? 16 : 0 }}
                  >
                    <Checkbox onChange={(e) => setWantsInvoice(e.target.checked)}>
                      <Text strong>{t('checkout.invoice.wantInvoice')}</Text>
                    </Checkbox>
                  </Form.Item>

                  {wantsInvoice && (
                    <>
                      <Alert
                        message={t('checkout.invoice.infoMessage')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={t('checkout.invoice.vatNumber')}
                            name="vat_number"
                            rules={[
                              { required: wantsInvoice, message: t('checkout.invoice.vatRequired') },
                              {
                                pattern: /^(EL)?\d{9}$|^[A-Z]{2}\d{8,12}$/,
                                message: t('checkout.invoice.vatInvalid'),
                              },
                            ]}
                          >
                            <Input placeholder="EL123456789" size="large" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={t('checkout.invoice.companyName')}
                            name="company_name"
                            rules={[
                              { required: wantsInvoice, message: t('checkout.invoice.companyRequired') },
                            ]}
                          >
                            <Input placeholder={t('checkout.invoice.companyPlaceholder')} size="large" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        label={t('checkout.invoice.profession')}
                        name="profession"
                      >
                        <Input placeholder={t('checkout.invoice.professionPlaceholder')} size="large" />
                      </Form.Item>
                      <Form.Item
                        label={t('checkout.invoice.taxOffice')}
                        name="tax_office"
                      >
                        <Input placeholder={t('checkout.invoice.taxOfficePlaceholder')} size="large" />
                      </Form.Item>
                    </>
                  )}
                </Card>

                {/* Give Back / NGO Selection */}
                {ngos.length > 0 && (
                  <Card
                    className="give-back-card"
                    style={{ marginBottom: 24, background: '#fff7e6', border: '1px solid #ffd591' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <HeartFilled style={{ color: '#eb2f96', fontSize: 20, marginRight: 8 }} />
                      <Text strong style={{ fontSize: 16 }}>{t('checkout.giveBack.title')}</Text>
                    </div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      {t('checkout.giveBack.description')}
                    </Text>

                    {/* Animap NGO Selection - Mandatory 2% donation */}
                    <div style={{ marginBottom: isUnifiedCheckout ? 0 : 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        <HeartFilled style={{ color: '#1890ff', marginRight: 8 }} />
                        {t('checkout.giveBack.animapDonation')} (2%)
                      </Text>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                        {t('checkout.giveBack.animapDonationInfo')}
                      </Text>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Select
                          placeholder={t('checkout.giveBack.selectNgo')}
                          size="large"
                          value={animapNgoId}
                          onChange={setAnimapNgoId}
                          loading={loadingNgos}
                          style={{ width: '100%' }}
                        >
                          {ngos.map((ngo) => (
                            <Select.Option key={ngo.id} value={ngo.id}>
                              <Space>
                                <HeartFilled style={{ color: '#eb2f96' }} />
                                {ngo.name}
                              </Space>
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>

                    {/* Impact Partner donation is handled internally by Animap - not shown to customers */}
                  </Card>
                )}

                {/* Cancellation Policy */}
                <Alert
                  message={
                    <span>
                      <ClockCircleOutlined style={{ marginRight: 8 }} />
                      {t('checkout.cancellationPolicy.title')}
                    </span>
                  }
                  description={t('checkout.cancellationPolicy.description')}
                  type="warning"
                  showIcon={false}
                  style={{ marginBottom: 16 }}
                />

                <Alert
                  message={t('checkout.paymentInfo.title')}
                  description={t('checkout.paymentInfo.description')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitting}
                  icon={<ShoppingOutlined />}
                  block
                >
                  {t('checkout.placeOrder')}
                </Button>
              </Form>
            </Card>
          </Col>

          {/* Order Summary */}
          <Col xs={24} lg={10}>
            <Card title={t('checkout.orderSummary')} className="order-summary-card">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {isUnifiedCheckout ? (
                  /* UNIFIED CHECKOUT - Show all partners */
                  <>
                    {cartStore.cart.map((group, index) => (
                      <div key={group.partner_id}>
                        {/* Seller Info */}
                        <div className="seller-info" style={{ marginBottom: 12 }}>
                          <Text type="secondary">
                            <EnvironmentOutlined /> {t('checkout.seller')}
                          </Text>
                          <Text strong>{group.partner_name}</Text>
                          {group.is_impact_partner && (
                            <Text type="success" style={{ fontSize: 12, marginLeft: 8 }}>
                              🤝 Impact Partner
                            </Text>
                          )}
                        </div>

                        {/* Items for this partner */}
                        <div className="order-items" style={{ marginBottom: 12 }}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {group.items.map((item) => (
                              <div key={item.id} className="order-item">
                                <div>
                                  <Text>{item.product_name}</Text>
                                  <br />
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {item.variant_size && `${item.variant_size} `}
                                    {item.variant_weight && `${item.variant_weight}kg `}
                                    × {item.quantity}
                                  </Text>
                                </div>
                                <Text strong>€{parseFloat(item.subtotal).toFixed(2)}</Text>
                              </div>
                            ))}
                          </Space>
                        </div>

                        {/* Partner subtotal */}
                        <div className="partner-subtotal" style={{ background: '#f5f5f5', padding: '8px 12px', borderRadius: 6, marginBottom: 8 }}>
                          <div className="total-row">
                            <Text type="secondary">{t('common.subtotal')}:</Text>
                            <Text>€{parseFloat(group.subtotal).toFixed(2)}</Text>
                          </div>
                          <div className="total-row">
                            <Text type="secondary">{t('checkout.shipping')}:</Text>
                            <Text>€{parseFloat(group.shipping_cost).toFixed(2)}</Text>
                          </div>
                        </div>

                        {index < cartStore.cart.length - 1 && <Divider />}
                      </div>
                    ))}

                    <Divider style={{ borderWidth: 2 }} />

                    {/* Combined Totals */}
                    <div className="order-totals">
                      <div className="total-row">
                        <Text type="secondary">{t('checkout.allItemsSubtotal')}:</Text>
                        <Text>€{unifiedSubtotal.toFixed(2)}</Text>
                      </div>
                      <div className="total-row">
                        <Text type="secondary">{t('checkout.totalShipping')}:</Text>
                        <Text>€{unifiedShipping.toFixed(2)}</Text>
                      </div>
                      <Divider />
                      <div className="total-row">
                        <Title level={4} style={{ margin: 0 }}>{t('common.total')}:</Title>
                        <Title level={4} style={{ margin: 0, color: '#FF9A76' }}>
                          €{unifiedTotal.toFixed(2)}
                        </Title>
                      </div>
                    </div>

                    {/* Info about single payment */}
                    <Alert
                      message={t('checkout.singlePaymentInfo')}
                      type="success"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  </>
                ) : (
                  /* SINGLE PARTNER CHECKOUT */
                  <>
                    {/* Seller Info */}
                    <div className="seller-info">
                      <Text type="secondary">
                        <EnvironmentOutlined /> {t('checkout.seller')}
                      </Text>
                      <Text strong>{partnerName}</Text>
                    </div>

                    <Divider />

                    {/* Items */}
                    <div className="order-items">
                      <Text strong>{t('checkout.items', { count: partnerGroup.item_count })}</Text>
                      <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 12 }}>
                        {partnerGroup.items.map((item) => (
                          <div key={item.id} className="order-item">
                            <div>
                              <Text>{item.product_name}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {item.variant_size && `${item.variant_size} `}
                                {item.variant_weight && `${item.variant_weight}kg `}
                                × {item.quantity}
                              </Text>
                            </div>
                            <Text strong>€{parseFloat(item.subtotal).toFixed(2)}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>

                    <Divider />

                    {/* Totals */}
                    <div className="order-totals">
                      <div className="total-row">
                        <Text type="secondary">{t('common.subtotal')}:</Text>
                        <Text>€{parseFloat(partnerGroup.subtotal).toFixed(2)}</Text>
                      </div>
                      <div className="total-row">
                        <Text type="secondary">{t('checkout.shipping')}:</Text>
                        <Text>€{parseFloat(partnerGroup.shipping_cost).toFixed(2)}</Text>
                      </div>
                      {appliedCoupon && (
                        <div className="total-row">
                          <Text type="secondary" style={{ color: '#52c41a' }}>
                            <TagOutlined style={{ marginRight: 4 }} />
                            {t('checkout.coupon.discount')} ({appliedCoupon.code}):
                          </Text>
                          <Text style={{ color: '#52c41a' }}>
                            -€{parseFloat(appliedCoupon.discount_amount).toFixed(2)}
                          </Text>
                        </div>
                      )}
                      <Divider />
                      <div className="total-row">
                        <Title level={4} style={{ margin: 0 }}>{t('common.total')}:</Title>
                        <Title level={4} style={{ margin: 0, color: '#FF9A76' }}>
                          €{(parseFloat(partnerGroup.total) - (appliedCoupon ? parseFloat(appliedCoupon.discount_amount) : 0)).toFixed(2)}
                        </Title>
                      </div>
                    </div>

                    {/* Donation Summary - Always shown (Animap 2% is mandatory) */}
                    <>
                      <Divider />
                      <div className="donation-summary" style={{ background: '#fff7e6', padding: 12, borderRadius: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                          <HeartFilled style={{ color: '#eb2f96', marginRight: 8 }} />
                          <Text strong>{t('checkout.giveBack.donationSummary')}</Text>
                        </div>
                        {/* Animap 2% donation - always shown */}
                        <div className="total-row" style={{ marginBottom: 4 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {t('checkout.giveBack.animapWillDonate')} (2%):
                          </Text>
                          <Text style={{ color: '#1890ff', fontWeight: 500 }}>
                            €{(parseFloat(partnerGroup.subtotal) * 0.02).toFixed(2)}
                          </Text>
                        </div>
                        {/* Partner donation (Impact Partner 1%) is not shown to customers */}
                        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                          {t('checkout.giveBack.donationNote')}
                        </Text>
                      </div>
                    </>
                  </>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
});

export default CheckoutPage;
